import { EllipsisOutlined } from "@ant-design/icons";
import { Button, Collapse, notification, Tag } from "antd";
import { Moment } from "moment";
import { useEffect, useState } from "react";

import { useGetNotesQuery } from "../../../../../api/graphql/queries/notes";
import {
  IUser,
  IUserDetails,
  KontaxUserStatus,
  KontaxUserStatusAdditionalParams,
  OnbStatus,
  PaymentFrequency,
  YesNoEnum,
} from "../../../../../types";
import {
  formatBooleanToString,
  getColorByOnboardingStatus,
} from "../../../../../utils";
import { getUserFullName } from "../../../../../utils/getUserFullName";
import ActionLogDrawerAsync from "../../../../common/ActionLogDrawerAsync";
import CustomerAttribute from "../../../../common/CustomerAttribute";
import EditableCustomerAttribute, {
  EditType,
} from "../../../../common/EditableCustomerAttribute";
import {
  CustomerAttributes,
  CustomerInformationCard,
  CustomerInformationSection,
  CustomerInformationSectionTitle,
  CustomerInformationSectionTitleWrapper,
  SectionTitle,
} from "../../../../common/styledComponents";
import TextWithHyperlinks from "../../../../common/TextWithHyperlinks";
import { DateInputType } from "../../../../common/TransactionFilters/DateInput";
import UserInfoDrawer, {
  UserInfoDrawerSources,
} from "../../../../common/UserInfoExcerpt/UserInfoDrawer";
import UserTaxInformationBlock from "../../../../common/UserTaxInformationBlock";
import UserBusinessInformationBlock from "../../Common/UserDetails/BusinessInformation";
import { ChangeKontaxStatusButton } from "../../Common/UserDetails/ChangeKontaxStatusButton";
import CustomerStatus from "../../Common/UserDetails/CustomerStatus";
import DependentsTable from "../../Common/UserDetails/DependentTable/DependentsTable";
import {
  ButtonsWrapper,
  HorizontalLine,
} from "../../Common/UserDetails/styledComponents";
import UserTaxNumbersList from "../../Common/UserDetails/TaxNumberList/TaxNumbersList";
import {
  formatDayMonthYear,
  formatMonthYear,
  formatOnbStatus,
  formatTimestamp,
} from "../../Common/UserDetails/utils";
import { PipedriveData, PipedriveDataContainer } from "../styledComponents";
import { getLastDeclarationDueDate } from "@/v1/components/common/helpers";

const Panel = Collapse.Panel;

export function CustomerInformation({
  user,
  editUser,
  updateUserStatus,
  updateDependents,
  deleteDependent,
}: {
  user: IUser;
  editUser: (email: string, userDetails: IUserDetails) => Promise<void>;
  updateUserStatus: (
    email: string,
    status: KontaxUserStatus | null,
    additionalParams?: KontaxUserStatusAdditionalParams
  ) => Promise<void>;
  updateDependents: Function;
  deleteDependent: (email: string, id: string) => Promise<void>;
}) {
  const [isUserInfoDrawerVisible, setIsUserInfoDrawerVisible] = useState(false);

  const { data: notes, error: getNotesError } = useGetNotesQuery({
    variables: { email: user.email },
  });

  useEffect(() => {
    if (getNotesError) {
      notification.error({
        message: `An error occurred while fetching the notes for the user ${user.email}`,
      });
    }
  }, [getNotesError, user.email]);

  const onSaveFibuStartDateHandler = (fibuStartDate: string) => {
    editUser(user.email, {
      fibuStartDate,
    });
  };

  const onSaveFibuEndDateHandler = (fibuEndDate: Date | null) => {
    const lastDeclarationDueDate = getLastDeclarationDueDate(
      fibuEndDate,
      user.vatPaymentFrequency as PaymentFrequency
    );
    editUser(user.email, {
      kontaxUser: {
        lastDeclarationDueDate,
      },
    });
  };

  const onSaveTaxDeclarationStartDateHandler = async (
    firstTaxDeclarationDate: Moment | null
  ) => {
    const year =
      firstTaxDeclarationDate === null ? null : firstTaxDeclarationDate.year();
    await editUser(user.email, {
      firstTaxDeclarationYear: year,
    });
  };

  const onSaveKontaxOnbStatusHandler = (onbStatus: OnbStatus) =>
    editUser(user.email, {
      kontaxUser: {
        onbStatus: onbStatus === OnbStatus.NOT_SET ? null : onbStatus,
      },
    });

  return (
    <>
      <CustomerInformationSection>
        <UserInfoDrawer
          visible={isUserInfoDrawerVisible}
          onClose={() => setIsUserInfoDrawerVisible(false)}
          user={user}
          notes={notes?.getNotes || []}
          source={UserInfoDrawerSources.ACCOUNTING}
        />
        <CustomerInformationSectionTitleWrapper>
          <CustomerInformationSectionTitle>
            Customer Information
          </CustomerInformationSectionTitle>
          <Button
            type="default"
            onClick={() => setIsUserInfoDrawerVisible(true)}
            icon={<EllipsisOutlined />}
          >
            More info
          </Button>
        </CustomerInformationSectionTitleWrapper>
        <CustomerInformationCard>
          <CustomerAttributes>
            <CustomerAttribute label="Name" withFixedIcon isCopyable>
              {getUserFullName(user)}
            </CustomerAttribute>
            <CustomerAttribute label="Status">
              <CustomerStatus status={user.kontaxStatus} />
            </CustomerAttribute>
            <div>
              <SectionTitle>
                <EditableCustomerAttribute
                  label="ONB Status"
                  dataTest="onbStatus"
                  editType={EditType.Select}
                  formatFunction={formatOnbStatus}
                  options={Object.values(OnbStatus)}
                  initialValue={user.kontaxUser?.onbStatus || OnbStatus.NOT_SET}
                  onSaveHandler={onSaveKontaxOnbStatusHandler}
                  renderAttributeContent={(value: OnbStatus) => {
                    return (
                      <Tag color={getColorByOnboardingStatus(value)}>
                        {formatOnbStatus(value).toUpperCase()}
                      </Tag>
                    );
                  }}
                  inputRequired
                  allowClear
                />
                <span>
                  <ActionLogDrawerAsync
                    title="Action log"
                    modelName="kontax_user"
                    recordIds={[user.id.toString()]}
                    small
                  />
                </span>
              </SectionTitle>
            </div>
            <CustomerAttribute label="Account ID" isCopyable withFixedIcon>
              {user.accountId}
            </CustomerAttribute>
            <CustomerAttribute label="Last Login At">
              {formatDayMonthYear(user.lastLoginAt)}
            </CustomerAttribute>
            <EditableCustomerAttribute
              label="FiBu onboarding completed"
              initialValue={formatBooleanToString(
                !!user.taxServiceOnboardingCompletedAt
              )}
              editType={EditType.Select}
              options={[...Object.values(YesNoEnum)]}
              onSaveHandler={async (value: YesNoEnum) => {
                const taxServiceOnboardingCompleted = value === YesNoEnum.YES;

                await editUser(user.email, {
                  taxServiceOnboardingCompleted,
                });
              }}
            />
            <EditableCustomerAttribute
              label="FIBU start date"
              initialValue={user.fibuStartDate}
              formatFunction={formatMonthYear}
              editType={DateInputType.Month}
              inputRequired
              onSaveHandler={onSaveFibuStartDateHandler}
              allowClear
            />
            <EditableCustomerAttribute
              label="FIBU end date"
              initialValue={user.kontaxUser?.lastDeclarationDueDate}
              formatFunction={formatMonthYear}
              editType={EditType.PeriodPicker}
              allowClear
              onSaveHandler={onSaveFibuEndDateHandler}
              vatPaymentFrequency={user.vatPaymentFrequency as PaymentFrequency}
            />
            <EditableCustomerAttribute
              label="Erste Steuererklärung"
              initialValue={user.firstTaxDeclarationYear?.toString()}
              editType={DateInputType.Year}
              allowClear
              onSaveHandler={onSaveTaxDeclarationStartDateHandler}
            />

            <PipedriveDataContainer>
              <Collapse defaultActiveKey={1}>
                <Panel header="Pipedrive data" key={1}>
                  <PipedriveData>
                    <TextWithHyperlinks
                      text={user?.kontaxUser?.pipedriveData}
                    />
                  </PipedriveData>
                </Panel>
              </Collapse>
            </PipedriveDataContainer>
          </CustomerAttributes>
        </CustomerInformationCard>

        <HorizontalLine />

        <UserTaxNumbersList user={user} />
        <HorizontalLine />

        <UserTaxInformationBlock
          user={user}
          editUser={editUser}
          isOnboarding={true}
        />
        <HorizontalLine />

        <CustomerInformationSectionTitle>
          Personal Information
        </CustomerInformationSectionTitle>
        <CustomerInformationCard>
          <CustomerAttributes>
            <CustomerAttribute label="Email address" isCopyable withFixedIcon>
              {user.email}
            </CustomerAttribute>
            <CustomerAttribute label="Geburtstag" withFixedIcon isCopyable>
              {formatDayMonthYear(user.birthDate)}
            </CustomerAttribute>
            <CustomerAttribute label="Address">
              {user.street}, {user.postCode} {user.city}
            </CustomerAttribute>
            <CustomerAttribute
              label="Steuerliche Identifikationsnummer"
              isCopyable
              withFixedIcon
            >
              {user.deTaxId}
            </CustomerAttribute>
            <CustomerAttribute label="IBAN" isCopyable withFixedIcon>
              {user.accountIban}
            </CustomerAttribute>
          </CustomerAttributes>
        </CustomerInformationCard>
        <HorizontalLine />

        <UserBusinessInformationBlock user={user} editUser={editUser} />
        <HorizontalLine />

        <CustomerInformationSectionTitle>
          User's Dependents
        </CustomerInformationSectionTitle>
        <DependentsTable
          email={user.email}
          dependents={user.dependents || []}
          updateDependents={updateDependents}
          deleteDependent={deleteDependent}
        />
        <HorizontalLine />

        <CustomerInformationSectionTitle>
          Self-onboarding Information
        </CustomerInformationSectionTitle>
        <CustomerInformationCard>
          <CustomerAttributes>
            <CustomerAttribute label="Profession">
              {user.profession}
            </CustomerAttribute>
            <CustomerAttribute label="Uses a second business account">
              {formatBooleanToString(user.hasSecondBusinessAccount)}
            </CustomerAttribute>
            <CustomerAttribute label="Terms and conditions accepted at">
              {formatTimestamp(user.taxAdvisoryTermsVersionAcceptedAt)}
            </CustomerAttribute>
            <CustomerAttribute label="Onboarding started at">
              {formatTimestamp(user.taxServiceOnboardingStartedAt)}
            </CustomerAttribute>
          </CustomerAttributes>
        </CustomerInformationCard>

        <HorizontalLine />

        <ButtonsWrapper>
          <ChangeKontaxStatusButton
            kontaxStatus={user.kontaxStatus}
            email={user.email}
            updateUserStatus={updateUserStatus}
          />
        </ButtonsWrapper>
      </CustomerInformationSection>
    </>
  );
}
